"use client";

import { useState } from "react";
import Link from "next/link";
import { Section, SectionHeading } from "@/components/platform/v2/Section";
import {
  ProductHero,
  ProductCta,
  NextSteps,
  RelatedCases,
} from "@/components/platform/v2/ProductKit";
import { EXTENSIONS } from "@/data/ecosystem";
import { PERIODS, formatPrice, type Period } from "@/data/pricing";

export default function ModulesPage() {
  const [period, setPeriod] = useState<Period>("12");
  return (
    <div className="vcentre-public pt-24 sm:pt-28 md:pt-32">
      <ProductHero
        eyebrow="Расширения VCENTRE.ONE"
        titleLines={["Добавляйте возможности", "когда они нужны бизнесу."]}
        lead="VCENTRE.ONE остаётся полноценной системой без расширений. Расширения добавляют специализированные механики в тот же контур клиентов, CRM, коммуникаций и данных."
        primary={{ label: "Смотреть расширения", to: "#catalog" }}
        secondary={{ label: "Что входит в VCENTRE.ONE", to: "/platform" }}
        visual={
          <div className="surface flex flex-col gap-5">
            <h2 className="display-3 text-primary">VCENTRE.ONE</h2>
            <p className="body-text">
              Клиенты · CRM · Продажи · Коммуникации · Каталог · Процессы ·
              Автоматизация
            </p>
            <div className="hairline" />
            <p className="body-text">
              Расширение использует тот же профиль клиента и общие данные.
              Самостоятельные продукты находятся в разделе «Платформы»;
              AI-баланс, облако и домены — в Магазине.
            </p>
          </div>
        }
      />
      <Section id="catalog" tone="quiet">
        <SectionHeading
          eyebrow="Каталог"
          title="Расширения под задачи бизнеса"
          text="Доступны с PRO для выбранного расширенного канала, в PRO+ и GENIUS — для приложения и сайта."
        />
        <div
          className="mx-auto mt-8 flex max-w-md rounded-xl border border-border p-1"
          role="group"
          aria-label="Период оплаты расширений"
        >
          {PERIODS.map((item) => (
            <button
              key={item.key}
              type="button"
              aria-pressed={period === item.key}
              onClick={() => setPeriod(item.key)}
              className={`flex-1 rounded-lg px-3 py-2 text-sm font-bold ${period === item.key ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
            >
              {item.label}
            </button>
          ))}
        </div>
        <div className="mt-8 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {EXTENSIONS.map((extension) => (
            <article
              key={extension.id}
              className="surface flex flex-col items-center gap-4 text-center"
            >
              <h3 className="text-lg font-extrabold">{extension.title}</h3>
              <p className="body-text">{extension.shortDescription}</p>
              {extension.availablePlans && (
                <p className="text-xs font-semibold text-primary">
                  Только PRO+ и GENIUS
                </p>
              )}
              <ul className="mx-auto space-y-2 text-sm text-foreground/85">
                {extension.features.map((feature) => (
                  <li key={feature}>• {feature}</li>
                ))}
              </ul>
              <p className="mt-auto pt-3 text-2xl font-bold text-primary">
                {formatPrice(extension.monthlyPrices[period])}
                <span className="text-sm text-muted-foreground"> / мес.</span>
              </p>
            </article>
          ))}
        </div>
      </Section>
      <Section tone="quiet">
        <SectionHeading
          eyebrow="Частые вопросы"
          title="Как работают расширения"
        />
        <div className="mx-auto mt-8 max-w-3xl space-y-5">
          {[
            [
              "Нужно ли покупать расширения?",
              "Нет. ONE включает полноценную систему управления и готовое стандартное приложение. Расширения подключаются под конкретные задачи.",
            ],
            [
              "Могут ли расширения работать отдельно?",
              "Нет. Расширения работают внутри VCENTRE.ONE.",
            ],
            [
              "Можно ли подключить несколько?",
              "Да. Учитывайте возможности выбранного клиентского канала и состав уже подключённых расширений.",
            ],
            [
              "Есть ли комиссия с билетов и аукционов?",
              "0% комиссии VCENTRE.ONE с продажи билетов и результата аукциона. Комиссии платёжных систем и эквайринга могут применяться отдельно.",
            ],
          ].map(([question, answer]) => (
            <details key={question} className="border-b border-border pb-4">
              <summary className="cursor-pointer font-bold">{question}</summary>
              <p className="body-text mt-3">{answer}</p>
            </details>
          ))}
        </div>
      </Section>
      <Section>
        <RelatedCases
          slugs={["business-forum", "dobrodomik"]}
          text="Проекты, где расширения дополняют основной процесс бизнеса."
        />
      </Section>
      <Section tone="quiet">
        <NextSteps
          items={[
            {
              to: "/platform/integrations",
              text: "Связь с внешними системами",
            },
            {
              to: "/platforms",
              label: "Платформы VCENTRE.IT",
              text: "Самостоятельные специализированные продукты",
            },
          ]}
        />
      </Section>
      <Section>
        <ProductCta
          title="Какие механики нужны вашему бизнесу?"
          text="Подберём расширения к текущему контуру ONE и уточним условия подключения на оставшийся период."
          buttonLabel="Подобрать расширения"
          to="/signin?focus=extensions"
          secondary={{ to: "/platform/pricing", label: "Тарифы ONE" }}
          visual={
            <img
              src="/assets/images/platform/modules-mascot-cta.png"
              alt="Маскот VCENTRE.ONE помогает расширить возможности бизнеса"
              width={1254}
              height={1254}
              loading="lazy"
              className="w-full max-w-[260px] drop-shadow-[0_24px_48px_rgba(0,0,0,0.55)]"
            />
          }
        />
      </Section>
    </div>
  );
}
